import React from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';
import {ChannelLogo} from '../channels/ChannelLogo';
import {mercadoAdsConnectionName} from '../channels/mercado_ads/mercadoAds';

// Slug del backend (/portal/me/connections/) → constante de canal para el logo.
export const CHANNEL_BY_SLUG = {
    google_ads: 'GOOGLE',
    facebook: 'FACEBOOK',
    ga4: 'ANALYTICS',
    tiktok: 'TIKTOK',
    linkedin: 'LINKEDIN',
    gsc: 'GSC',
    tienda_nube: 'TIENDA_NUBE',
    shopify: 'SHOPIFY',
    escalafy: 'ESCALAFY',
    mercado_ads: 'MERCADO_ADS',
};

// Color de marca de cada canal — tiñe el tile del logo + el accent del hover. Le da identidad
// a cada card sin romper la paleta warm del portal (el tinte se aplica al 10-16%).
const CHANNEL_ACCENT_BY_SLUG = {
    google_ads: '#1AA85D',
    facebook: '#3B5998',
    ga4: '#E7B042',
    tiktok: '#1A1A1A',
    linkedin: '#0A66C2',
    gsc: '#4285F4',
    tienda_nube: '#28A7E9',
    shopify: '#5E8E3E',
    escalafy: '#1F2A44',
    mercado_ads: '#0A0080',
};

// Íconos: solo nombres registrados en init_icons_library.js (FA5). 'not_connected' usa un dot CSS.
const STATUS_META = {
    connected: {icon: 'circle-check', key: 'status_connected'},
    broken: {icon: 'exclamation-triangle', key: 'status_broken'},
    not_connected: {icon: null, key: 'status_not_connected'},
};

// Cuentas/tiendas ya vinculadas (solo se muestran cuando la conexión está activa). En Mercado Ads, el apodo
// del vendedor: el nombre de fábrica de la conexión es su id numérico de Mercado Libre.
const connectedAccounts = (connection, slug) => {
    if (connection.kind === 'store' && Array.isArray(connection.stores)) {
        return connection.stores.map((s) => s.name).filter(Boolean);
    }
    // Rentabilidad (Escalafy): las tiendas que ESTE cliente compartió con su agencia.
    if (connection.kind === 'profitability' && Array.isArray(connection.organizations)) {
        return connection.organizations.map((o) => o.name).filter(Boolean);
    }
    if (Array.isArray(connection.credentials)) {
        return connection.credentials
            .map((c) => (slug === 'mercado_ads' ? mercadoAdsConnectionName(c) : c.label))
            .filter(Boolean);
    }
    return [];
};

/**
 * Tarjeta de una plataforma/tienda en el Portal del cliente. Logo en tile con tinte de marca +
 * estado (pill cálido) + cuentas conectadas + CTA según estado (Conectar / Reconectar / Desconectar).
 * La lógica de conexión vive en PortalConnections.
 *
 * `notice` es lo que pasó al conectar ESTA plataforma (un error con qué hacer): va en la tarjeta,
 * donde ocurrió, y no en un toast que se va.
 */
export const PortalConnectionCard = ({slug, connection, onConnect, onDisconnect, busy, index = 0, description = null,
                                      notice = null}) => {
    const {t} = useTranslation();
    const status = connection.status || 'not_connected';
    const meta = STATUS_META[status] || STATUS_META.not_connected;
    const channelCode = CHANNEL_BY_SLUG[slug];
    const accent = CHANNEL_ACCENT_BY_SLUG[slug] || '#A69E94';
    const accounts = status === 'connected' ? connectedAccounts(connection, slug) : [];

    return (
        <div
            className={`portal-connection-card -${status}`}
            style={{'--card-accent': accent, animationDelay: `${index * 55}ms`}}
        >
            <span className="portal-connection-card__accent" aria-hidden="true"/>

            <div className="portal-connection-card__top">
                <div className="portal-connection-card__media">
                    {channelCode && <ChannelLogo channel={channelCode}/>}
                </div>
                <span className="portal-connection-card__pill">
                    {meta.icon
                        ? <FaIcon iconName={meta.icon}/>
                        : <span className="portal-connection-card__dot" aria-hidden="true"/>}
                    <span>{t(`portal_connections.${meta.key}`)}</span>
                </span>
            </div>

            <div className="portal-connection-card__body">
                <span className="portal-connection-card__name">{connection.name}</span>
                {description && <p className="portal-connection-card__desc">{description}</p>}
                {notice}
                {accounts.length > 0 && (
                    <ul className="portal-connection-card__accounts">
                        {accounts.slice(0, 3).map((label, i) => (
                            <li key={i} title={label}>{label}</li>
                        ))}
                        {accounts.length > 3 && (
                            <li className="-more">{t('portal_connections.more_accounts', {count: accounts.length - 3})}</li>
                        )}
                    </ul>
                )}
            </div>

            <div className="portal-connection-card__actions">
                {status === 'connected' && (
                    <button className="portal-connection-card__btn -ghost" disabled={busy} onClick={onDisconnect}>
                        {t('portal_connections.disconnect')}
                    </button>
                )}
                {status === 'broken' && (
                    <button className="portal-connection-card__btn -warning" disabled={busy} onClick={onConnect}>
                        <FaIcon iconName="sync-alt"/>
                        {t('portal_connections.reconnect')}
                    </button>
                )}
                {status === 'not_connected' && (
                    <button className="portal-connection-card__btn -primary" disabled={busy} onClick={onConnect}>
                        {t('portal_connections.connect')}
                    </button>
                )}
            </div>
        </div>
    );
};
